import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { SortEvent } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { FgaIconComponent } from '../fga-icon/fga-icon.component';
import {
  resolveTableRowTypeLabel,
  TABLE_ROW_ICON,
  TableMode,
  TableRow,
  TableRowIcon,
} from './fga-table.model';
import { ENTITY_TYPE, MEMBERSHIP_MODE } from '../../models/entity.model';
import { isScimManagedGroupId } from '../../models/group.model';
import { EllipsisTitleDirective } from '../../directives/ellipsis-title.directive';
import { compareByName } from '../../utils/sorting';

@Component({
  selector: 'fga-table',
  imports: [CommonModule, FgaIconComponent, ButtonModule, TableModule, TooltipModule, EllipsisTitleDirective],
  templateUrl: './fga-table.component.html',
  styleUrls: ['./fga-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FgaTableComponent {
  @Input() rows: TableRow[] = [];
  @Input() viewMode: TableMode = MEMBERSHIP_MODE.MEMBERS;
  @Input() hideRemove = false;

  @Output() remove = new EventEmitter<TableRow>();

  removeClicked(row: TableRow): void {
    if (this.hideRemove) {
      return;
    }
    this.remove.emit(row);
  }

  /** Explicit removal action text, scoped per dashboard (Members vs Owners). */
  removeTooltip(): string {
    return this.viewMode === MEMBERSHIP_MODE.OWNERS ? 'Remove owner' : 'Remove member';
  }

  removeAriaLabel(row: TableRow): string {
    return `${this.removeTooltip()} ${row.name}`;
  }

  rowIcon(row: TableRow): TableRowIcon | null {
    switch (row.type) {
      case ENTITY_TYPE.GROUP:
        return isScimManagedGroupId(row.id) ? TABLE_ROW_ICON.SCIM_GROUP : TABLE_ROW_ICON.LOCAL_GROUP;
      case ENTITY_TYPE.M2M:
        return TABLE_ROW_ICON.M2M;
      case ENTITY_TYPE.USER:
      default:
        return TABLE_ROW_ICON.USER;
    }
  }

  rowTypeLabel(row: TableRow): string {
    return resolveTableRowTypeLabel(row);
  }

  customSort(event: SortEvent): void {
    const data = (event.data ?? []) as TableRow[];
    const order = event.order ?? 1;
    const field = this.resolveSortField(event.field);

    const sortedRows = data
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        const comparison = field === 'type'
          ? this.compareType(a.row, b.row)
          : this.compareByField(a.row, b.row, field);

        if (comparison !== 0) {
          return order * comparison;
        }

        return a.index - b.index;
      })
      .map(({ row }) => row);

    sortedRows.forEach((row, index) => {
      data[index] = row;
    });
  }

  private compareType(a: TableRow, b: TableRow): number {
    return compareByName(
      { name: this.rowTypeLabel(a) },
      { name: this.rowTypeLabel(b) },
    );
  }

  private isTableRowField(field: string): field is keyof TableRow {
    return field === 'id' || field === 'name' || field === 'email' || field === 'type';
  }

  private resolveSortField(field?: string): keyof TableRow {
    if (field && this.isTableRowField(field)) {
      return field;
    }

    return 'name';
  }

  // Mirrors PrimeNG's default single-column comparator; every TableRow field is a string or undefined.
  private compareByField(a: TableRow, b: TableRow, field: keyof TableRow): number {
    const valueA = a[field];
    const valueB = b[field];

    if (valueA == null || valueB == null) {
      if (valueA == null && valueB == null) {
        return 0;
      }

      return valueA == null ? -1 : 1;
    }

    return valueA.localeCompare(valueB);
  }
}
